import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { DICT, type Dict, type Lang } from "./dictionary";

type Ctx = {
 lang: Lang;
 setLang: (l: Lang) => void;
 t: Dict;
};

const I18nContext = createContext<Ctx | null>(null);
const STORAGE_KEY = "heallexa_lang";

function detectInitial(): Lang {
 if (typeof window === "undefined") return "en";
 try {
 const saved = localStorage.getItem(STORAGE_KEY);
 if (saved === "es" || saved === "en") return saved;
 } catch {}
 const nav = (navigator.language || "en").toLowerCase();
 return nav.startsWith("es") ? "es" : "en";
}

export function I18nProvider({ children }: { children: ReactNode }) {
 const [lang, setLangState] = useState<Lang>("en");

 useEffect(() => {
 const initial = detectInitial();
 setLangState(initial);
 if (typeof document !== "undefined") document.documentElement.lang = initial;
 }, []);

 const setLang = (l: Lang) => {
 setLangState(l);
 try { localStorage.setItem(STORAGE_KEY, l); } catch {}
 if (typeof document !== "undefined") document.documentElement.lang = l;
 };

 const value = useMemo<Ctx>(() => ({ lang, setLang, t: DICT[lang] }), [lang]);
 return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
 const ctx = useContext(I18nContext);
 if (!ctx) {
 // SSR / outside provider fallback, return English so render still works.
 return { lang: "en" as Lang, setLang: () => {}, t: DICT.en };
 }
 return ctx;
}
